/**
 * Clip-derived queries the timeline needs: which frames carry keys, what the dope-sheet rows
 * are, and where the previous/next key sits. Pure functions of the store state — no DOM.
 */

import type { CanonicalAnimationClip, CanonicalBoneId } from '../../shared';
import { selectVisibleBones } from '../index';
import type { EditorState } from '../index';
import type { SheetRow } from './layout';

export function boneKeyFrames(
  clip: CanonicalAnimationClip | null,
  bone: CanonicalBoneId,
): number[] {
  if (!clip) return [];
  return (clip.tracks[bone]?.keyframes ?? []).map((key) => key.frame);
}

/** Frames where at least one of `bones` has a key, ascending and unique (the "All" row). */
export function summaryFrames(
  clip: CanonicalAnimationClip | null,
  bones: readonly CanonicalBoneId[],
): number[] {
  if (!clip) return [];
  const seen = new Set<number>();
  for (const bone of bones) {
    for (const key of clip.tracks[bone]?.keyframes ?? []) seen.add(key.frame);
  }
  return [...seen].sort((a, b) => a - b);
}

/** Every keyed frame in the clip, ascending and unique (drives ⏪ / ⏩ and `,` / `.`). */
export function allKeyFrames(clip: CanonicalAnimationClip | null): number[] {
  if (!clip) return [];
  const seen = new Set<number>();
  for (const track of Object.values(clip.tracks)) {
    for (const key of track?.keyframes ?? []) seen.add(key.frame);
  }
  return [...seen].sort((a, b) => a - b);
}

/** The dope-sheet lanes for the current `timeline.visibleBones` filter, in hierarchy order. */
export function sheetRows(state: EditorState): SheetRow[] {
  const clip = state.clip;
  return selectVisibleBones(state).map((bone) => ({
    bone,
    frames: boneKeyFrames(clip, bone),
  }));
}

/** Largest frame in `frames` strictly below `from`, or `null`. */
export function prevKeyFrame(frames: readonly number[], from: number): number | null {
  let best: number | null = null;
  for (const frame of frames) {
    if (frame < from && (best === null || frame > best)) best = frame;
  }
  return best;
}

/** Smallest frame in `frames` strictly above `from`, or `null`. */
export function nextKeyFrame(frames: readonly number[], from: number): number | null {
  let best: number | null = null;
  for (const frame of frames) {
    if (frame > from && (best === null || frame < best)) best = frame;
  }
  return best;
}

/** `${bone}:${frame}` — the key used for selection lookups while drawing. */
export function refKey(bone: CanonicalBoneId | string, frame: number): string {
  return `${bone}:${frame}`;
}

export function selectionKeySet(state: EditorState): Set<string> {
  return new Set(state.keySelection.map((ref) => refKey(ref.bone, ref.frame)));
}
